import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
	netsuiteService,
	VendorOption,
	LocationOption,
	DepartmentOption,
	ProductionLineOption,
	ClassOption,
	AccountOption,
	CustomerOption,
	ExpenseLinePayload,
	UpdatePOExpenseChange,
	UpdatePOExpenseOperation,
	PurchaseOrderDetails,
} from "../services/netsuiteService";

export const CreatePO: React.FC = () => {
	const { internalId } = useParams<{ internalId: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const copyFrom = (location.state as any)?.copyFrom ?? null;
	const { accounts } = useMsal();
	const isAuthenticated = accounts.length > 0;

	// Generate unique ID for React keys
	const generateUniqueId = (): string => {
		return `line_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	};

	// Determine if we're in edit mode
	const editMode = !!internalId;

	// Form state
	const [vendors, setVendors] = useState<VendorOption[]>([]);
	const [selectedVendor, setSelectedVendor] = useState<string>("");
	const [searchText, setSearchText] = useState<string>("");
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [receiveByDate, setReceiveByDate] = useState<string>("");
	const [locations, setLocations] = useState<LocationOption[]>([]);
	const [selectedLocation, setSelectedLocation] = useState<string>("");
	const [locationSearchText, setLocationSearchText] = useState<string>("");
	const [showLocationDropdown, setShowLocationDropdown] =
		useState<boolean>(false);
	const [departments, setDepartments] = useState<DepartmentOption[]>([]);
	const [selectedDepartment, setSelectedDepartment] = useState<string>("");
	const [departmentSearchText, setDepartmentSearchText] = useState<string>("");
	const [showDepartmentDropdown, setShowDepartmentDropdown] =
		useState<boolean>(false);
	const [productionLines, setProductionLines] = useState<
		ProductionLineOption[]
	>([]);
	const [selectedProductionLine, setSelectedProductionLine] =
		useState<string>("");
	const [productionLineSearchText, setProductionLineSearchText] =
		useState<string>("");
	const [showProductionLineDropdown, setShowProductionLineDropdown] =
		useState<boolean>(false);

	const [classes, setClasses] = useState<ClassOption[]>([]);

	// Expense line items state
	interface ExpenseLine {
		id: string;
		isExisting: boolean;
		sourceLineId?: string;
		sourceLineUniqueKey?: string;
		selectedAccount: string;
		accountSearchText: string;
		showAccountDropdown: boolean;
		selectedCustomer: string;
		customerSearchText: string;
		showCustomerDropdown: boolean;
		amount: string;
		memo: string;
		selectedDepartment: string;
		departmentSearchText: string;
		showDepartmentDropdown: boolean;
		selectedClass: string;
		classSearchText: string;
		showClassDropdown: boolean;
	}

	const [accountsList, setAccountsList] = useState<AccountOption[]>([]);
	const [customersList, setCustomersList] = useState<CustomerOption[]>([]);
	const [expenseList, setexpenseList] = useState<ExpenseLine[]>([
		{
			id: `line_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
			isExisting: false,
			sourceLineId: "",
			sourceLineUniqueKey: "",
			selectedAccount: "",
			accountSearchText: "",
			showAccountDropdown: false,
			selectedCustomer: "",
			customerSearchText: "",
			showCustomerDropdown: false,
			amount: "",
			memo: "",
			selectedDepartment: "",
			departmentSearchText: "",
			showDepartmentDropdown: false,
			selectedClass: "",
			classSearchText: "",
			showClassDropdown: false,
		},
	]);
	const [removedExpenseLines, setRemovedExpenseLines] = useState<
		Array<{ lineId?: string; lineUniqueKey?: string }>
	>([]);

	const [memo, setMemo] = useState<string>("");
	const [uploadedFiles, setUploadedFiles] = useState<
		Array<{
			name: string;
			type: string;
			data: string;
		}>
	>([]);

	const [loading, setLoading] = useState<boolean>(true);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [showForm, setShowForm] = useState<boolean>(true);
	const [poApprovalStatus, setPoApprovalStatus] = useState<string | null>(null);
	const [rejectionReason, setRejectionReason] = useState<string | null>(null);
	const [comingSoonMode] = useState<boolean>(false); // Just read-only

	// Account numbers that require production line to be selected
	const accountsRequiringProductionLine = [
		"150010",
		"150020",
		"150030",
		"150040",
		"150050",
		"150060",
	];

	// Maximum number of expense line items allowed
	const MAX_EXPENSE_LINES = 22;
	const MAX_UPLOAD_FILES = 10;

	// Refs for click-outside detection
	const vendorDropdownRef = useRef<HTMLDivElement>(null);
	const locationDropdownRef = useRef<HTMLDivElement>(null);
	const departmentDropdownRef = useRef<HTMLDivElement>(null);
	const productionLineDropdownRef = useRef<HTMLDivElement>(null);
	const expenseLineDepartmentDropdownRefs = useRef<{
		[key: string]: HTMLDivElement | null;
	}>({});
	const expenseLineClassDropdownRefs = useRef<{
		[key: string]: HTMLDivElement | null;
	}>({});
	const expenseLineAccountDropdownRefs = useRef<{
		[key: string]: HTMLDivElement | null;
	}>({});
	const expenseLineCustomerDropdownRefs = useRef<{
		[key: string]: HTMLDivElement | null;
	}>({});

	// Filter vendors based on search text
	const filteredVendors = vendors
		.filter((vendor) => {
			const searchLower = (searchText || "").toLowerCase();
			const name = (vendor.name || "").toLowerCase();
			const address = (vendor.address || "").toLowerCase();
			const city = (vendor.city || "").toLowerCase();
			const state = (vendor.state || "").toLowerCase();
			return (
				name.includes(searchLower) ||
				address.includes(searchLower) ||
				city.includes(searchLower) ||
				state.includes(searchLower)
			);
		})
		.filter(
			(vendor, index, self) =>
				// Deduplicate by vendor ID
				index === self.findIndex((v) => v.id === vendor.id),
		);

	// Header department dropdown only shows departments enabled for PO approval settings.
	const filteredHeaderDepartments = departments
		.filter((department) => department.hasPoApprovalSettings)
		.filter((department) =>
			(department.name || "")
				.toLowerCase()
				.includes((departmentSearchText || "").toLowerCase()),
		);

	// Filter departments for a specific expense line based on that line's search text
	const getFilteredExpenseLineDepartments = (
		lineDepartmentSearchText: string,
	) =>
		departments.filter((department) =>
			(department.name || "")
				.toLowerCase()
				.includes((lineDepartmentSearchText || "").toLowerCase()),
		);

	const handleVendorSelect = (vendorId: string, vendorName: string) => {
		setSelectedVendor(vendorId);
		setSearchText(vendorName);
		setShowDropdown(false);
	};

	const handleLocationSelect = (locationId: string, locationName: string) => {
		setSelectedLocation(locationId);
		setLocationSearchText(locationName);
		setShowLocationDropdown(false);
	};

	const handleDepartmentSelect = (
		departmentId: string,
		departmentName: string,
	) => {
		setSelectedDepartment(departmentId);
		setDepartmentSearchText(departmentName);
		setShowDepartmentDropdown(false);
	};

	const handleProductionLineSelect = (
		productionLineId: string,
		productionLineName: string,
	) => {
		setSelectedProductionLine(productionLineId);
		setProductionLineSearchText(productionLineName);
		setShowProductionLineDropdown(false);
	};

	// Handle expense line updates
	const handleExpenseLineAccountSelect = (
		lineId: string,
		accountId: string,
		accountName: string,
	) => {
		setexpenseList((prev) =>
			prev.map((line) =>
				line.id === lineId
					? {
							...line,
							selectedAccount: accountId,
							accountSearchText: accountName,
							showAccountDropdown: false,
						}
					: line,
			),
		);
	};

	const handleExpenseLineCustomerSelect = (
		lineId: string,
		customerId: string,
		customerName: string,
		classId?: string,
	) => {
		setexpenseList((prev) =>
			prev.map((line) =>
				line.id === lineId
					? {
							...line,
							selectedCustomer: customerId,
							customerSearchText: customerName,
							showCustomerDropdown: false,
							...(classId && {
								selectedClass: classId,
								classSearchText:
									classes.find((c) => c.id === classId)?.name || "",
							}),
						}
					: line,
			),
		);
	};

	const handleExpenseLineDepartmentSelect = (
		lineId: string,
		departmentId: string,
		departmentName: string,
	) => {
		setexpenseList((prev) =>
			prev.map((line) =>
				line.id === lineId
					? {
							...line,
							selectedDepartment: departmentId,
							departmentSearchText: departmentName,
							showDepartmentDropdown: false,
						}
					: line,
			),
		);
	};

	const handleExpenseLineClassSelect = (
		lineId: string,
		classId: string,
		className: string,
	) => {
		setexpenseList((prev) =>
			prev.map((line) =>
				line.id === lineId
					? {
							...line,
							selectedClass: classId,
							classSearchText: className,
							showClassDropdown: false,
						}
					: line,
			),
		);
	};

	const updateExpenseLineField = (
		lineId: string,
		field: keyof ExpenseLine,
		value: any,
	) => {
		setexpenseList((prev) =>
			prev.map((line) =>
				line.id === lineId ? { ...line, [field]: value } : line,
			),
		);
	};

	const addExpenseLine = () => {
		const newId = generateUniqueId();
		setexpenseList((prev) => [
			...prev,
			{
				id: newId,
				isExisting: false,
				sourceLineId: "",
				sourceLineUniqueKey: "",
				selectedAccount: "",
				accountSearchText: "",
				showAccountDropdown: false,
				selectedCustomer: "",
				customerSearchText: "",
				showCustomerDropdown: false,
				amount: "",
				memo: "",
				selectedDepartment: selectedDepartment,
				departmentSearchText: departmentSearchText,
				showDepartmentDropdown: false,
				selectedClass: "",
				classSearchText: "",
				showClassDropdown: false,
			},
		]);
	};

	const copyExpenseLine = () => {
		if (expenseList.length === 0 || expenseList.length >= MAX_EXPENSE_LINES)
			return;
		const lastLine = expenseList[expenseList.length - 1];
		const newId = generateUniqueId();
		setexpenseList((prev) => [
			...prev,
			{
				id: newId,
				isExisting: false,
				sourceLineId: "",
				sourceLineUniqueKey: "",
				selectedAccount: lastLine.selectedAccount,
				accountSearchText: lastLine.accountSearchText,
				showAccountDropdown: false,
				selectedCustomer: lastLine.selectedCustomer,
				customerSearchText: lastLine.customerSearchText,
				showCustomerDropdown: false,
				amount: lastLine.amount,
				memo: lastLine.memo,
				selectedDepartment: lastLine.selectedDepartment,
				departmentSearchText: lastLine.departmentSearchText,
				showDepartmentDropdown: false,
				selectedClass: lastLine.selectedClass,
				classSearchText: lastLine.classSearchText,
				showClassDropdown: false,
			},
		]);
	};

	const removeExpenseLine = (lineId: string) => {
		if (expenseList.length > 1) {
			const removedLine = expenseList.find((line) => line.id === lineId);
			if (!removedLine) {
				return;
			}
			if (
				editMode &&
				removedLine.isExisting &&
				(removedLine.sourceLineId || removedLine.sourceLineUniqueKey)
			) {
				setRemovedExpenseLines((prev) => [
					...prev,
					{
						lineId: removedLine.sourceLineId,
						lineUniqueKey: removedLine.sourceLineUniqueKey,
					},
				]);
			}
			setexpenseList((prev) => prev.filter((line) => line.id !== lineId));
		}
	};

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Check if maximum files reached
		if (uploadedFiles.length >= MAX_UPLOAD_FILES) {
			alert(`You can upload a maximum of ${MAX_UPLOAD_FILES} files.`);
			return;
		}

		// Validate file type
		const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];
		if (!allowedTypes.includes(file.type)) {
			alert("Invalid file type. Please upload a PNG, JPG, or PDF file.");
			return;
		}

		// Validate file size (max 10MB)
		const maxSize = 10 * 1024 * 1024;
		if (file.size > maxSize) {
			alert("File is too large. Maximum size is 10MB.");
			return;
		}

		// Read file and convert to base64
		const reader = new FileReader();
		reader.onload = (event) => {
			const base64String = (event.target?.result as string)?.split(",")[1];
			setUploadedFiles((prev) => [
				...prev,
				{
					name: file.name,
					type: file.type,
					data: base64String,
				},
			]);
		};
		reader.readAsDataURL(file);
	};

	const handleRemoveFile = (index: number) => {
		setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
	};

	// Fetch vendors, locations, and departments on component mount
	useEffect(() => {
		const loadData = async () => {
			try {
				setLoading(true);
				setFetchError(null);
				const [
					vendorList,
					locationList,
					departmentList,
					productionLineList,
					classList,
					accountList,
					customerList,
				] = await Promise.all([
					netsuiteService.fetchVendors(),
					netsuiteService.fetchLocations(),
					netsuiteService.fetchDepartments(),
					netsuiteService.fetchProductionLines(),
					netsuiteService.fetchClasses(),
					netsuiteService.fetchAccounts(),
					netsuiteService.fetchCustomers(),
				]);
				setVendors(vendorList);
				setLocations(locationList);
				setDepartments(departmentList);
				setProductionLines(productionLineList);
				setClasses(classList);
				setAccountsList(accountList);
				setCustomersList(customerList);

				// If in edit mode, fetch PO details
				if (editMode && internalId && accounts[0]?.username) {
					try {
						const displayName = accounts[0]?.name || "";

						// Check authorization first
						await netsuiteService.checkPOAuthorization(
							internalId,
							displayName,
							accounts[0].username,
						);

						const poDetails = (await netsuiteService.fetchPurchaseOrders(
							accounts[0].username,
							internalId,
						)) as PurchaseOrderDetails;

						// Helper function to find display name by ID from a list
						const findNameById = (
							id: string,
							list: Array<{ id: string; name: string }> | undefined,
						): string => {
							if (!list || !id) return id || "";
							const item = list.find((l) => l && l.id === id);
							const result = item?.name || id;
							return result;
						};

						// Populate form with PO data, matching IDs to display names
						setSelectedVendor(poDetails.vendorId || "");
						const vendorName = findNameById(poDetails.vendorId, vendorList);
						setSearchText(vendorName);
						setReceiveByDate(poDetails.dueDate || "");
						setSelectedLocation(poDetails.locationId || "");
						const locationName = findNameById(
							poDetails.locationId,
							locationList,
						);
						setLocationSearchText(locationName);
						setSelectedDepartment(poDetails.departmentId || "");
						const deptName = findNameById(
							poDetails.departmentId,
							departmentList,
						);
						setDepartmentSearchText(deptName);
						setSelectedProductionLine(poDetails.productionLineId || "");
						const prodLineName = findNameById(
							poDetails.productionLineId,
							productionLineList,
						);
						setProductionLineSearchText(prodLineName);
						setMemo(poDetails.memo || "");

						// Store the approval status and rejection reason
						setPoApprovalStatus(poDetails.approvalStatus || null);
						setRejectionReason((poDetails as any).rejectionReason || null);

						// Populate expense lines
						if (poDetails.expenseLines && poDetails.expenseLines.length > 0) {
							setRemovedExpenseLines([]);
							const newExpenseList = poDetails.expenseLines.map((line) => {
								const accountOption = accountList?.find(
									(a) => a && a.id === line.accountId,
								);
								const customerOption = customerList?.find(
									(c) => c && c.id === line.customerId,
								);
								const deptOption = departmentList?.find(
									(d) => d && d.id === line.departmentId,
								);
								const classOption = classList?.find(
									(c) => c && c.id === line.classId,
								);

								const accountDisplayText = accountOption
									? `${accountOption.number || ""} ${accountOption.name || ""}`.trim()
									: line.account || "";

								return {
									id: generateUniqueId(),
									isExisting: true,
									sourceLineId: line.lineId || "",
									sourceLineUniqueKey: line.lineUniqueKey || "",
									selectedAccount: line.accountId || "",
									accountSearchText: accountDisplayText,
									showAccountDropdown: false,
									selectedCustomer: line.customerId || "",
									customerSearchText:
										customerOption?.name || line.customer || "",
									showCustomerDropdown: false,
									amount: line.amount?.toString() || "",
									memo: line.memo || "",
									selectedDepartment: line.departmentId || "",
									departmentSearchText:
										deptOption?.name || line.department || "",
									showDepartmentDropdown: false,
									selectedClass: line.classId || "",
									classSearchText: classOption?.name || line.class || "",
									showClassDropdown: false,
								};
							});
							setexpenseList(newExpenseList);
						}
					} catch (error: any) {
						setFetchError(
							error.message || "Failed to load purchase order details",
						);
						console.error("Error loading PO details:", error);
					}
				}
			} catch (error: any) {
				setFetchError(error.message || "Failed to load form data");
				console.error("Error loading data:", error);
			} finally {
				setLoading(false);
			}
		};

		if (isAuthenticated) {
			loadData();
		}
	}, [isAuthenticated, editMode, internalId, accounts]);

	// Populate form from a copied PO (passed via navigation state)
	useEffect(() => {
		if (!copyFrom || editMode || loading) return;

		const applyDetails = async () => {
			let details: PurchaseOrderDetails | null = null;
			try {
				details = (await netsuiteService.fetchPurchaseOrders(
					accounts[0]?.username || "",
					copyFrom.internalId,
				)) as unknown as PurchaseOrderDetails;
			} catch {
				// fall back to basic PO fields if detail fetch fails
			}

			const po = details ?? copyFrom;

			setSelectedVendor(po.vendorId || copyFrom.vendorId || "");
			const vendorMatch = vendors.find(
				(v) => v.id === (po.vendorId || copyFrom.vendorId),
			);
			setSearchText(vendorMatch?.name || copyFrom.vendor || "");

			// dueDate from details is already YYYY-MM-DD; fallback from basic PO
			setReceiveByDate(po.dueDate || copyFrom.dueDate || "");

			const locationId = po.locationId || copyFrom.locationId || "";
			const locationMatch = locations.find(
				(l) => l.id === locationId || l.name === copyFrom.location,
			);
			if (locationMatch) {
				setSelectedLocation(locationMatch.id);
				setLocationSearchText(locationMatch.name);
			}

			// Body department
			if (details?.departmentId) {
				const deptMatch = departments.find(
					(d) => d.id === details!.departmentId,
				);
				setSelectedDepartment(details.departmentId);
				setDepartmentSearchText(deptMatch?.name || details.department || "");
			}

			setMemo(""); // blank memo per spec

			const lines = details?.expenseLines ?? copyFrom.lineItems ?? [];
			if (lines.length > 0) {
				setRemovedExpenseLines([]);
				const copied = lines.map((line: any) => {
					const accountOption =
						accountsList.find(
							(a) => a.id === (line.accountId || line.account),
						) ?? accountsList.find((a) => a.name === line.account);
					const customerOption =
						customersList.find(
							(c) => c.id === (line.customerId || line.customer),
						) ?? customersList.find((c) => c.name === line.customer);
					const deptOption = departments.find(
						(d) => d.id === line.departmentId || d.name === line.department,
					);
					const classOption = classes.find(
						(c) => c.id === line.classId || c.name === line.class,
					);
					return {
						id: generateUniqueId(),
						isExisting: false,
						sourceLineId: "",
						sourceLineUniqueKey: "",
						selectedAccount: accountOption?.id || "",
						accountSearchText: accountOption
							? `${accountOption.number || ""} ${accountOption.name || ""}`.trim()
							: line.account || "",
						showAccountDropdown: false,
						selectedCustomer: customerOption?.id || "",
						customerSearchText: customerOption?.name || line.customer || "",
						showCustomerDropdown: false,
						amount: "", // blank per spec
						memo: "", // blank per spec
						selectedDepartment: deptOption?.id || line.departmentId || "",
						departmentSearchText: deptOption?.name || line.department || "",
						showDepartmentDropdown: false,
						selectedClass: classOption?.id || line.classId || "",
						classSearchText: classOption?.name || line.class || "",
						showClassDropdown: false,
					};
				});
				setexpenseList(copied);
			}
		};

		applyDetails();
	}, [
		copyFrom,
		editMode,
		loading,
		vendors,
		locations,
		departments,
		accountsList,
		customersList,
		classes,
		accounts,
	]);

	// Reset form state when leaving edit mode
	useEffect(() => {
		if (!editMode) {
			// Reset all form fields to empty
			setSelectedVendor("");
			setSearchText("");
			setReceiveByDate("");
			setSelectedLocation("");
			setLocationSearchText("");
			setSelectedDepartment("");
			setDepartmentSearchText("");
			setSelectedProductionLine("");
			setProductionLineSearchText("");
			setMemo("");
			setUploadedFiles([]);
			setPoApprovalStatus(null);
			setRejectionReason(null);
			setexpenseList([
				{
					id: "1",
					isExisting: false,
					sourceLineId: "",
					sourceLineUniqueKey: "",
					selectedAccount: "",
					accountSearchText: "",
					showAccountDropdown: false,
					selectedCustomer: "",
					customerSearchText: "",
					showCustomerDropdown: false,
					amount: "",
					memo: "",
					selectedDepartment: "",
					departmentSearchText: "",
					showDepartmentDropdown: false,
					selectedClass: "",
					classSearchText: "",
					showClassDropdown: false,
				},
			]);
			setRemovedExpenseLines([]);
		}
	}, [editMode]);

	// Handle click outside dropdowns
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;

			// Check vendor dropdown
			if (
				vendorDropdownRef.current &&
				!vendorDropdownRef.current.contains(target)
			) {
				setShowDropdown(false);
			}

			// Check location dropdown
			if (
				locationDropdownRef.current &&
				!locationDropdownRef.current.contains(target)
			) {
				setShowLocationDropdown(false);
			}

			// Check department dropdown
			if (
				departmentDropdownRef.current &&
				!departmentDropdownRef.current.contains(target)
			) {
				setShowDepartmentDropdown(false);
			}

			// Check production line dropdown
			if (
				productionLineDropdownRef.current &&
				!productionLineDropdownRef.current.contains(target)
			) {
				setShowProductionLineDropdown(false);
			}

			// Check expense line department dropdowns
			Object.entries(expenseLineDepartmentDropdownRefs.current).forEach(
				([lineId, ref]) => {
					if (ref && !ref.contains(target)) {
						updateExpenseLineField(lineId, "showDepartmentDropdown", false);
					}
				},
			);

			// Check expense line class dropdowns
			Object.entries(expenseLineClassDropdownRefs.current).forEach(
				([lineId, ref]) => {
					if (ref && !ref.contains(target)) {
						updateExpenseLineField(lineId, "showClassDropdown", false);
					}
				},
			);

			// Check expense line account dropdowns
			Object.entries(expenseLineAccountDropdownRefs.current).forEach(
				([lineId, ref]) => {
					if (ref && !ref.contains(target)) {
						updateExpenseLineField(lineId, "showAccountDropdown", false);
					}
				},
			);

			// Check expense line customer dropdowns
			Object.entries(expenseLineCustomerDropdownRefs.current).forEach(
				([lineId, ref]) => {
					if (ref && !ref.contains(target)) {
						updateExpenseLineField(lineId, "showCustomerDropdown", false);
					}
				},
			);
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Auto-populate department on expense lines when main form values change
	useEffect(() => {
		if (selectedDepartment) {
			setexpenseList((prev) =>
				prev.map((line) => {
					const isLineDepartmentEmpty =
						!line.selectedDepartment &&
						!(line.departmentSearchText || "").trim();

					if (!isLineDepartmentEmpty) {
						return line;
					}

					return {
						...line,
						selectedDepartment: selectedDepartment,
						departmentSearchText: departmentSearchText,
					};
				}),
			);
		}
	}, [selectedDepartment]);

	// Handle form submission
	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!selectedVendor && !editMode) {
			setSubmitError("Please select a vendor");
			return;
		}

		try {
			setSubmitting(true);
			setSubmitError(null);
			setSubmitSuccess(false);

			// Transform expense lines to the format expected by the service
			const formattedexpenseList: ExpenseLinePayload[] = expenseList.map(
				(line) => ({
					account: line.selectedAccount,
					customer: line.selectedCustomer,
					amount: parseFloat(line.amount.replace(/,/g, "")),
					memo: line.memo,
					department: line.selectedDepartment,
					class: line.selectedClass,
				}),
			);

			let result;
			if (editMode && internalId) {
				const hasUnidentifiedExistingLines = expenseList.some(
					(line) =>
						line.isExisting && !line.sourceLineId && !line.sourceLineUniqueKey,
				);

				if (hasUnidentifiedExistingLines) {
					setSubmitError(
						"This PO line data is missing NetSuite line identifiers. Update blocked to avoid duplicating lines. Ask the RESTlet to return sys_id (preferred, mapped as lineUniqueKey) and line for each expense line in purchaseorder detail responses.",
					);
					setSubmitting(false);
					return;
				}

				const expenseChanges: UpdatePOExpenseChange[] = [
					...expenseList.map((line) => ({
						operation: (line.isExisting
							? "update"
							: "add") as UpdatePOExpenseOperation,
						lineId: line.sourceLineId || undefined,
						lineUniqueKey: line.sourceLineUniqueKey || undefined,
						account: line.selectedAccount,
						customer: line.selectedCustomer,
						amount: parseFloat(line.amount.replace(/,/g, "")),
						memo: line.memo,
						department: line.selectedDepartment,
						class: line.selectedClass,
					})),
					...removedExpenseLines.map((removed) => ({
						operation: "delete" as UpdatePOExpenseOperation,
						lineId: removed.lineId || undefined,
						lineUniqueKey: removed.lineUniqueKey || undefined,
					})),
				];

				// Update existing PO
				result = await netsuiteService.updatePO({
					internalId: internalId,
					vendor: selectedVendor,
					duedate: receiveByDate,
					location: selectedLocation,
					department: selectedDepartment,
					productionLine: selectedProductionLine,
					memo: memo,
					accountName: accounts[0]?.name || "",
					userName: accounts[0]?.username || "",
					expenseList: formattedexpenseList,
					expenseChanges,
					...(uploadedFiles.length > 0 && { attachments: uploadedFiles }),
				});
			} else {
				// Create new PO
				result = await netsuiteService.createPO({
					vendor: selectedVendor,
					duedate: receiveByDate,
					location: selectedLocation,
					department: selectedDepartment,
					productionLine: selectedProductionLine,
					memo: memo,
					accountName: accounts[0]?.name || "",
					userName: accounts[0]?.username || "",
					expenseList: formattedexpenseList,
					...(uploadedFiles.length > 0 && { attachments: uploadedFiles }),
				});
			}

			console.log("📋 PO operation result:", result);

			if (result.success) {
				// Navigate to PO list filtered to this PO by internal ID
				const poInternalId = result.recordId || internalId;
				navigate(
					`/po-list${poInternalId ? `?internalId=${poInternalId}` : ""}`,
				);
			} else {
				console.error("❌ PO operation failed:", result.message);
				setSubmitError(result.message);
			}
		} catch (error: any) {
			setSubmitError(error.message || "Failed to save PO");
			console.error("Error saving PO:", error);
		} finally {
			setSubmitting(false);
		}
	};

	if (!isAuthenticated) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
				<div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full mx-4">
					<h2 className="text-2xl font-bold text-gray-900 mb-4">
						Access Denied
					</h2>
					<p className="text-gray-600">
						Please sign in to access the Create PO page.
					</p>
					<a
						href="/"
						className="inline-block mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
					>
						Go to Home
					</a>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-6xl mx-auto">
				{comingSoonMode ? (
					<div className="bg-white rounded-lg shadow-md p-8 min-h-96 flex flex-col items-center justify-center">
						<div className="text-center">
							<p className="text-6xl mb-4">🚀</p>
							<h1 className="text-4xl font-bold text-gray-900 mb-4">
								Coming Soon
							</h1>
							<p className="text-xl text-gray-600 mb-8">
								This feature is under development. Stay tuned!
							</p>
						</div>
					</div>
				) : (
					<>
						{/* Page Header */}
						<div className="mb-8">
							<h1 className="text-4xl font-bold text-gray-900 mb-2">
								{editMode
									? poApprovalStatus === "Rejected"
										? "Edit Purchase Order and re-submit"
										: "Edit Purchase Order"
									: "Create Purchase Order"}
							</h1>
							<p className="text-gray-600">
								{editMode
									? poApprovalStatus === "Rejected"
										? "Update the details of your purchase order and re-submit it for approval in NetSuite"
										: "Update the details of your purchase order in NetSuite"
									: "Fill in the details below to create a new purchase order in NetSuite"}
							</p>

							{/* Rejection Reason Alert */}
							{editMode &&
								!submitSuccess &&
								poApprovalStatus === "Rejected" &&
								rejectionReason && (
									<div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
										<p className="text-sm font-semibold text-red-800 mb-1">
											Reason for Rejection
										</p>
										<p className="text-sm text-red-700">{rejectionReason}</p>
									</div>
								)}
						</div>

						{/* Form Card */}
						<div className="bg-white rounded-lg shadow-md p-8">
							{/* Success Message with Create New PO Link */}
							{submitSuccess && !showForm && (
								<div className="text-center py-12">
									<div className="mb-6">
										<p className="text-6xl mb-4">✓</p>
										<p className="text-2xl font-bold text-green-600 mb-2">
											🎉 Purchase order {editMode ? "updated" : "created"}{" "}
											successfully!
										</p>
										<p className="text-gray-600">
											Your PO has been{" "}
											{editMode
												? "updated and submitted to NetSuite"
												: "created and submitted to NetSuite"}
											.
										</p>
									</div>
									<div className="flex gap-4 justify-center">
										{editMode ? (
											<a
												href="/po-list"
												className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition inline-block"
											>
												Go to PO List
											</a>
										) : (
											<button
												onClick={() => {
													setSubmitSuccess(false);
													setShowForm(true);
													setSubmitError(null);
												}}
												className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
											>
												Create New PO
											</button>
										)}
										<a
											href="/po-list?reset=true"
											className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition flex items-center"
										>
											Go to PO List
										</a>
									</div>
								</div>
							)}

							{/* Error Messages - Display outside form */}
							{fetchError && (
								<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
									<p className="text-red-800 font-semibold">
										Error loading form data:
									</p>
									<p className="text-sm text-red-700 mt-1">{fetchError}</p>
									<p className="text-xs text-red-600 mt-2">
										Please check that your NetSuite account has the necessary
										permissions and try again.
									</p>
								</div>
							)}

							{/* Form Section */}
							{showForm && !fetchError && (
								<>
									{/* Success Message */}
									{submitSuccess && (
										<div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
											<p className="text-green-800 font-semibold">
												🎉 Purchase order created successfully!
											</p>
										</div>
									)}

									{submitError && (
										<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
											<p className="text-red-800 font-semibold">
												{submitError}
											</p>
										</div>
									)}

									{/* Form */}
									<form onSubmit={handleSubmit} className="space-y-6">
										{/* Form Body Section */}
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div>
												{/* Vendor Search */}
												<div ref={vendorDropdownRef}>
													<label
														htmlFor="vendor"
														className="block text-sm font-medium text-gray-700 mb-2"
													>
														Vendor <span className="text-red-600">*</span>
													</label>
													<div className="relative">
														{loading ? (
															<div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed flex items-center">
																<span className="inline-block animate-spin mr-2">
																	⏳
																</span>
																Loading vendors...
															</div>
														) : (
															<>
																<input
																	type="text"
																	id="vendor"
																	placeholder="Search vendors..."
																	value={searchText}
																	autoComplete="off"
																	onChange={(e) => {
																		setSearchText(e.target.value);
																		setShowDropdown(true);
																	}}
																	onFocus={() => {
																		setShowDropdown(true);
																	}}
																	disabled={submitting}
																	className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer"
																/>

																{/* Dropdown List */}
																{showDropdown && (
																	<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
																		{filteredVendors.length > 0 ? (
																			filteredVendors.map((vendor) => (
																				<button
																					key={vendor.id}
																					type="button"
																					onClick={() =>
																						handleVendorSelect(
																							vendor.id,
																							vendor.name,
																						)
																					}
																					className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
																				>
																					<div className="font-medium text-gray-900">
																						{vendor.name}
																					</div>
																					<div className="text-xs text-gray-500">
																						{vendor.address &&
																						vendor.city &&
																						vendor.state
																							? `${vendor.address}, ${vendor.city}, ${vendor.state}${vendor.zip ? ` ${vendor.zip}` : ""}`
																							: `ID: ${vendor.id}`}
																					</div>
																				</button>
																			))
																		) : (
																			<div className="px-4 py-3 text-center text-gray-500">
																				No vendors found
																			</div>
																		)}
																	</div>
																)}
															</>
														)}
													</div>
												</div>

												{/* Receive By Date */}
												<div className="mt-4">
													<label
														htmlFor="receiveby"
														className="block text-sm font-medium text-gray-700 mb-2"
													>
														Receive By <span className="text-red-600">*</span>
													</label>
													<input
														type="date"
														id="receiveby"
														value={receiveByDate}
														onChange={(e) => setReceiveByDate(e.target.value)}
														disabled={submitting}
														className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
													/>
												</div>

												{/* Location Dropdown */}
												<div className="mt-4" ref={locationDropdownRef}>
													<label
														htmlFor="location"
														className="block text-sm font-medium text-gray-700 mb-2"
													>
														Location <span className="text-red-600">*</span>
													</label>
													<div className="relative">
														{loading ? (
															<div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed flex items-center">
																<span className="inline-block animate-spin mr-2">
																	⏳
																</span>
																Loading locations...
															</div>
														) : (
															<>
																<button
																	type="button"
																	id="location"
																	onClick={() =>
																		setShowLocationDropdown(
																			!showLocationDropdown,
																		)
																	}
																	disabled={submitting}
																	className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer text-left bg-white hover:bg-gray-50"
																>
																	{locationSearchText || "Select a location..."}
																</button>

																{/* Dropdown List */}
																{showLocationDropdown && (
																	<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-40 max-h-60 overflow-y-auto">
																		{locations.length > 0 ? (
																			locations.map((location) => (
																				<button
																					key={location.id}
																					type="button"
																					onClick={() =>
																						handleLocationSelect(
																							location.id,
																							location.name,
																						)
																					}
																					className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
																				>
																					<div className="font-medium text-gray-900">
																						{location.name}
																					</div>
																					<div className="text-xs text-gray-500">
																						ID: {location.id}
																					</div>
																				</button>
																			))
																		) : (
																			<div className="px-4 py-3 text-center text-gray-500">
																				No locations found
																			</div>
																		)}
																	</div>
																)}
															</>
														)}
													</div>
												</div>
											</div>
											<div>
												{/* Department Search */}
												<div ref={departmentDropdownRef}>
													<label
														htmlFor="department"
														className="block text-sm font-medium text-gray-700 mb-2"
													>
														Department <span className="text-red-600">*</span>
													</label>
													<div className="relative flex gap-2">
														{loading ? (
															<div className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed flex items-center">
																<span className="inline-block animate-spin mr-2">
																	⏳
																</span>
																Loading departments...
															</div>
														) : (
															<>
																<input
																	type="text"
																	id="department"
																	placeholder="Search departments..."
																	value={departmentSearchText}
																	title={
																		departmentSearchText || "Search departments"
																	}
																	autoComplete="off"
																	onChange={(e) => {
																		setDepartmentSearchText(e.target.value);
																		setShowDepartmentDropdown(true);
																	}}
																	onFocus={() =>
																		setShowDepartmentDropdown(true)
																	}
																	disabled={submitting}
																	className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer"
																/>

																{departmentSearchText && (
																	<button
																		type="button"
																		onClick={() => {
																			setSelectedDepartment("");
																			setDepartmentSearchText("");
																			setShowDepartmentDropdown(false);
																		}}
																		disabled={submitting}
																		className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 text-red-600 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
																	>
																		Clear
																	</button>
																)}

																{/* Dropdown List */}
																{showDepartmentDropdown && (
																	<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 max-h-60 overflow-y-auto">
																		{filteredHeaderDepartments.length > 0 ? (
																			filteredHeaderDepartments.map(
																				(department) => (
																					<button
																						key={department.id}
																						type="button"
																						title={department.name}
																						onClick={() =>
																							handleDepartmentSelect(
																								department.id,
																								department.name,
																							)
																						}
																						className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
																					>
																						<div className="font-medium text-gray-900">
																							{department.name}
																						</div>
																						<div className="text-xs text-gray-500">
																							ID: {department.id}
																						</div>
																					</button>
																				),
																			)
																		) : (
																			<div className="px-4 py-3 text-center text-gray-500">
																				No departments found
																			</div>
																		)}
																	</div>
																)}
															</>
														)}
													</div>
												</div>
												{/* Production Line Dropdown */}
												<div className="mt-4" ref={productionLineDropdownRef}>
													<label
														htmlFor="productionline"
														className="block text-sm font-medium text-gray-700 mb-2"
													>
														Production Line No{" "}
														{expenseList.some((line: ExpenseLine) =>
															accountsRequiringProductionLine.some(
																(accountNum) =>
																	line.accountSearchText.startsWith(accountNum),
															),
														) && <span className="text-red-600">*</span>}
													</label>
													<div className="relative flex gap-2">
														{loading ? (
															<div className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed flex items-center">
																<span className="inline-block animate-spin mr-2">
																	⏳
																</span>
																Loading production lines...
															</div>
														) : (
															<>
																<button
																	type="button"
																	id="productionline"
																	onClick={() =>
																		setShowProductionLineDropdown(
																			!showProductionLineDropdown,
																		)
																	}
																	disabled={submitting}
																	className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer text-left bg-white hover:bg-gray-50"
																>
																	{productionLineSearchText ||
																		"Select a production line..."}
																</button>
																{productionLineSearchText && (
																	<button
																		type="button"
																		onClick={() => {
																			setSelectedProductionLine("");
																			setProductionLineSearchText("");
																			setShowProductionLineDropdown(false);
																		}}
																		disabled={submitting}
																		className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 text-red-600 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
																	>
																		Clear
																	</button>
																)}

																{/* Dropdown List */}
																{showProductionLineDropdown && (
																	<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
																		{productionLines.length > 0 ? (
																			productionLines.map((prodline) => (
																				<button
																					key={prodline.id}
																					type="button"
																					onClick={() =>
																						handleProductionLineSelect(
																							prodline.id,
																							prodline.name,
																						)
																					}
																					className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
																				>
																					<div className="font-medium text-gray-900">
																						{prodline.name}
																					</div>
																					<div className="text-xs text-gray-500">
																						ID: {prodline.id}
																					</div>
																				</button>
																			))
																		) : (
																			<div className="px-4 py-3 text-center text-gray-500">
																				No production lines found
																			</div>
																		)}
																	</div>
																)}
															</>
														)}
													</div>
												</div>
												{/* Memo Field */}
												<div className="mt-4">
													<label
														htmlFor="memo"
														className="block text-sm font-medium text-gray-700 mb-2"
													>
														Memo
													</label>
													<input
														type="text"
														id="memo"
														value={memo}
														autoComplete="off"
														onChange={(e) => setMemo(e.target.value)}
														placeholder="Enter any memo text (optional)"
														disabled={submitting}
														className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
													/>
												</div>
											</div>
										</div>

										{/* File Upload Section */}
										<div className="border-t border-gray-300 pt-6 mt-6">
											<h2 className="text-lg font-semibold text-gray-900 mb-4">
												Attachments (Max {MAX_UPLOAD_FILES} files)
											</h2>
											<div className="max-w-md">
												<label className="block text-sm font-medium text-gray-700 mb-2">
													Upload File (PNG, JPG, PDF)
												</label>
												<div className="relative">
													<input
														type="file"
														accept=".png,.jpg,.jpeg,.pdf"
														onChange={handleFileUpload}
														disabled={
															submitting ||
															uploadedFiles.length >= MAX_UPLOAD_FILES
														}
														className="block w-full text-sm text-gray-500
													file:mr-4 file:py-2 file:px-4
													file:rounded-lg file:border-0
													file:text-sm file:font-semibold
													file:bg-blue-50 file:text-blue-700
													hover:file:bg-blue-100
													disabled:opacity-50 disabled:cursor-not-allowed"
													/>
												</div>
												{uploadedFiles.length > 0 && (
													<div className="mt-4 space-y-3">
														{uploadedFiles.map((file) => (
															<div
																key={`${file.name}_${file.data.length}`}
																className="p-4 bg-green-50 border border-green-200 rounded-lg"
															>
																<div className="flex items-start justify-between">
																	<div>
																		<p className="font-medium text-gray-900">
																			✓ {file.name}
																		</p>
																		<p className="text-sm text-gray-600 mt-1">
																			File type: {file.type}
																		</p>
																		<p className="text-xs text-gray-500 mt-1">
																			Size:{" "}
																			{(
																				(file.data.length * 0.75) /
																				1024
																			).toFixed(2)}{" "}
																			KB
																		</p>
																	</div>
																	<button
																		type="button"
																		onClick={() => {
																			const idx = uploadedFiles.findIndex(
																				(f) =>
																					f.name === file.name &&
																					f.data.length === file.data.length,
																			);
																			if (idx !== -1) handleRemoveFile(idx);
																		}}
																		disabled={submitting}
																		className="text-red-600 hover:text-red-800 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
																	>
																		Remove
																	</button>
																</div>
															</div>
														))}
													</div>
												)}
												{uploadedFiles.length >= MAX_UPLOAD_FILES && (
													<p className="mt-2 text-sm text-gray-500">
														Maximum of {MAX_UPLOAD_FILES} files reached
													</p>
												)}
											</div>
										</div>

										{/* Expense Line Items Section */}
										<div className="border-t border-gray-300 pt-6 mt-6">
											<h2 className="text-lg font-semibold text-gray-900 mb-4">
												Expense Line Items
											</h2>

											{expenseList.map((line, index) => (
												<div
													key={line.id}
													className="mb-6 p-4 border border-gray-200 rounded-lg"
												>
													<div className="flex justify-between items-center mb-4">
														<h3 className="font-medium text-gray-700">
															Line {index + 1}
														</h3>
														{expenseList.length > 1 && (
															<button
																type="button"
																onClick={() => removeExpenseLine(line.id)}
																className="text-red-600 hover:text-red-800 text-sm font-medium"
															>
																Remove
															</button>
														)}
													</div>

													<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
														{/* Account Dropdown */}
														<div
															ref={(el) => {
																if (el)
																	expenseLineAccountDropdownRefs.current[
																		line.id
																	] = el;
															}}
														>
															<label
																htmlFor={`account-${line.id}`}
																className="block text-sm font-medium text-gray-700 mb-2"
															>
																Account <span className="text-red-600">*</span>
															</label>
															<div className="relative">
																<input
																	type="text"
																	id={`account-${line.id}`}
																	placeholder="Search accounts..."
																	value={line.accountSearchText}
																	autoComplete="off"
																	onChange={(e) => {
																		updateExpenseLineField(
																			line.id,
																			"accountSearchText",
																			e.target.value,
																		);
																		updateExpenseLineField(
																			line.id,
																			"showAccountDropdown",
																			true,
																		);
																	}}
																	onFocus={() =>
																		updateExpenseLineField(
																			line.id,
																			"showAccountDropdown",
																			true,
																		)
																	}
																	disabled={submitting}
																	className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer"
																/>

																{line.showAccountDropdown && (
																	<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
																		{accountsList
																			.filter(
																				(acc: AccountOption) =>
																					(acc.number || "")
																						.toLowerCase()
																						.includes(
																							line.accountSearchText.toLowerCase(),
																						) ||
																					(acc.name || "")
																						.toLowerCase()
																						.includes(
																							line.accountSearchText.toLowerCase(),
																						),
																			)
																			.map((account: AccountOption) => (
																				<button
																					key={account.id}
																					type="button"
																					onClick={() =>
																						handleExpenseLineAccountSelect(
																							line.id,
																							account.id,
																							`${account.number} ${account.name}`,
																						)
																					}
																					className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
																				>
																					<div className="font-medium text-gray-900">
																						{account.number} {account.name}
																					</div>
																					<div className="text-xs text-gray-500">
																						ID: {account.id}
																					</div>
																				</button>
																			))}
																		{accountsList.filter(
																			(acc: AccountOption) =>
																				(acc.number || "")
																					.toLowerCase()
																					.includes(
																						line.accountSearchText.toLowerCase(),
																					) ||
																				(acc.name || "")
																					.toLowerCase()
																					.includes(
																						line.accountSearchText.toLowerCase(),
																					),
																		).length === 0 && (
																			<div className="px-4 py-3 text-center text-gray-500">
																				No accounts found
																			</div>
																		)}
																	</div>
																)}
															</div>
														</div>

														{/* Customer Dropdown */}
														<div
															ref={(el) => {
																if (el)
																	expenseLineCustomerDropdownRefs.current[
																		line.id
																	] = el;
															}}
														>
															<label
																htmlFor={`customer-${line.id}`}
																className="block text-sm font-medium text-gray-700 mb-2"
															>
																Customer
															</label>
															<div className="relative flex gap-2">
																<input
																	type="text"
																	id={`customer-${line.id}`}
																	placeholder="Search customers..."
																	value={line.customerSearchText}
																	autoComplete="off"
																	onChange={(e) => {
																		updateExpenseLineField(
																			line.id,
																			"customerSearchText",
																			e.target.value,
																		);
																		updateExpenseLineField(
																			line.id,
																			"showCustomerDropdown",
																			true,
																		);
																	}}
																	onFocus={() =>
																		updateExpenseLineField(
																			line.id,
																			"showCustomerDropdown",
																			true,
																		)
																	}
																	disabled={submitting}
																	className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer"
																/>

																{line.customerSearchText && (
																	<button
																		type="button"
																		onClick={() => {
																			updateExpenseLineField(
																				line.id,
																				"selectedCustomer",
																				"",
																			);
																			updateExpenseLineField(
																				line.id,
																				"customerSearchText",
																				"",
																			);
																			updateExpenseLineField(
																				line.id,
																				"showCustomerDropdown",
																				false,
																			);
																		}}
																		disabled={submitting}
																		className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 text-red-600 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
																	>
																		Clear
																	</button>
																)}

																{line.showCustomerDropdown && (
																	<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
																		{customersList
																			.filter((cust: CustomerOption) =>
																				(cust.name || "")
																					.toLowerCase()
																					.includes(
																						line.customerSearchText.toLowerCase(),
																					),
																			)
																			.map((customer: CustomerOption) => (
																				<button
																					key={customer.id}
																					type="button"
																					onClick={() =>
																						handleExpenseLineCustomerSelect(
																							line.id,
																							customer.id,
																							customer.name,
																							customer.classId,
																						)
																					}
																					className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
																				>
																					<div className="font-medium text-gray-900">
																						{customer.name}
																					</div>
																					<div className="text-xs text-gray-500">
																						ID: {customer.id}
																					</div>
																				</button>
																			))}
																		{customersList.filter(
																			(cust: CustomerOption) =>
																				(cust.name || "")
																					.toLowerCase()
																					.includes(
																						line.customerSearchText.toLowerCase(),
																					),
																		).length === 0 && (
																			<div className="px-4 py-3 text-center text-gray-500">
																				No customers found
																			</div>
																		)}
																	</div>
																)}
															</div>
														</div>

														{/* Amount Field */}
														<div>
															<label
																htmlFor={`amount-${line.id}`}
																className="block text-sm font-medium text-gray-700 mb-2"
															>
																Amount <span className="text-red-600">*</span>
															</label>
															<div className="relative">
																<span className="absolute left-4 top-2 text-gray-700 font-medium pointer-events-none">
																	$
																</span>
																<input
																	type="text"
																	inputMode="decimal"
																	id={`amount-${line.id}`}
																	placeholder="0.00"
																	value={line.amount}
																	onChange={(e) => {
																		let value = e.target.value;
																		// Remove non-digit and non-decimal characters (but keep commas temporarily)
																		value = value.replace(/[^\d.,]/g, "");
																		// Remove commas for processing
																		value = value.replace(/,/g, "");
																		// Ensure only one decimal point
																		const parts = value.split(".");
																		if (parts.length > 2) {
																			value =
																				parts[0] +
																				"." +
																				parts.slice(1).join("");
																		}
																		// Limit to 2 decimal places
																		if (parts[1] && parts[1].length > 2) {
																			value =
																				parts[0] +
																				"." +
																				parts[1].substring(0, 2);
																		}
																		updateExpenseLineField(
																			line.id,
																			"amount",
																			value,
																		);
																	}}
																	onBlur={(e) => {
																		const value = e.target.value;
																		if (value && !isNaN(parseFloat(value))) {
																			const formatted = parseFloat(
																				value,
																			).toLocaleString("en-US", {
																				minimumFractionDigits: 2,
																				maximumFractionDigits: 2,
																			});
																			updateExpenseLineField(
																				line.id,
																				"amount",
																				formatted,
																			);
																		}
																	}}
																	disabled={submitting}
																	className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-right font-medium"
																/>
															</div>
														</div>
													</div>
													<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
														{/* Memo Field */}
														<div>
															<label
																htmlFor={`memo-${line.id}`}
																className="block text-sm font-medium text-gray-700 mb-2"
															>
																Memo <span className="text-red-600">*</span>
															</label>
															<input
																type="text"
																id={`memo-${line.id}`}
																placeholder="Enter memo..."
																autoComplete="off"
																value={line.memo}
																onChange={(e) => {
																	updateExpenseLineField(
																		line.id,
																		"memo",
																		e.target.value,
																	);
																}}
																disabled={submitting}
																className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
															/>
														</div>

														{/* Department Dropdown */}
														<div
															ref={(el) => {
																if (el)
																	expenseLineDepartmentDropdownRefs.current[
																		line.id
																	] = el;
															}}
														>
															<label
																htmlFor={`expense-department-${line.id}`}
																className="block text-sm font-medium text-gray-700 mb-2"
															>
																Department{" "}
																<span className="text-red-600">*</span>
															</label>
															<div className="relative flex gap-2">
																<input
																	type="text"
																	id={`expense-department-${line.id}`}
																	placeholder="Search departments..."
																	value={line.departmentSearchText}
																	title={
																		line.departmentSearchText ||
																		"Search departments"
																	}
																	autoComplete="off"
																	onChange={(e) => {
																		updateExpenseLineField(
																			line.id,
																			"departmentSearchText",
																			e.target.value,
																		);
																		updateExpenseLineField(
																			line.id,
																			"showDepartmentDropdown",
																			true,
																		);
																	}}
																	onFocus={() =>
																		updateExpenseLineField(
																			line.id,
																			"showDepartmentDropdown",
																			true,
																		)
																	}
																	disabled={submitting}
																	className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer"
																/>

																{line.departmentSearchText && (
																	<button
																		type="button"
																		onClick={() => {
																			updateExpenseLineField(
																				line.id,
																				"selectedDepartment",
																				"",
																			);
																			updateExpenseLineField(
																				line.id,
																				"departmentSearchText",
																				"",
																			);
																			updateExpenseLineField(
																				line.id,
																				"showDepartmentDropdown",
																				false,
																			);
																		}}
																		disabled={submitting}
																		className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 text-red-600 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
																	>
																		Clear
																	</button>
																)}

																{line.showDepartmentDropdown && (
																	<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
																		{getFilteredExpenseLineDepartments(
																			line.departmentSearchText,
																		).length > 0 ? (
																			getFilteredExpenseLineDepartments(
																				line.departmentSearchText,
																			).map((department) => (
																				<button
																					key={department.id}
																					type="button"
																					title={department.name}
																					onClick={() =>
																						handleExpenseLineDepartmentSelect(
																							line.id,
																							department.id,
																							department.name,
																						)
																					}
																					className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
																				>
																					<div className="font-medium text-gray-900">
																						{department.name}
																					</div>
																					<div className="text-xs text-gray-500">
																						ID: {department.id}
																					</div>
																				</button>
																			))
																		) : (
																			<div className="px-4 py-3 text-center text-gray-500">
																				No departments found
																			</div>
																		)}
																	</div>
																)}
															</div>
														</div>

														{/* Class Dropdown */}
														<div
															ref={(el) => {
																if (el)
																	expenseLineClassDropdownRefs.current[
																		line.id
																	] = el;
															}}
														>
															<label
																htmlFor={`expense-class-${line.id}`}
																className="block text-sm font-medium text-gray-700 mb-2"
															>
																Class
															</label>
															<div className="relative flex gap-2">
																<button
																	type="button"
																	id={`expense-class-${line.id}`}
																	onClick={() =>
																		updateExpenseLineField(
																			line.id,
																			"showClassDropdown",
																			!line.showClassDropdown,
																		)
																	}
																	disabled={submitting}
																	className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer text-left bg-white hover:bg-gray-50"
																>
																	{line.classSearchText || "Select a class..."}
																</button>

																{line.classSearchText && (
																	<button
																		type="button"
																		onClick={() => {
																			updateExpenseLineField(
																				line.id,
																				"selectedClass",
																				"",
																			);
																			updateExpenseLineField(
																				line.id,
																				"classSearchText",
																				"",
																			);
																			updateExpenseLineField(
																				line.id,
																				"showClassDropdown",
																				false,
																			);
																		}}
																		disabled={submitting}
																		className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 text-red-600 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
																	>
																		Clear
																	</button>
																)}

																{line.showClassDropdown && (
																	<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
																		{classes.length > 0 ? (
																			classes.map((cls) => (
																				<button
																					key={cls.id}
																					type="button"
																					onClick={() =>
																						handleExpenseLineClassSelect(
																							line.id,
																							cls.id,
																							cls.name,
																						)
																					}
																					className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
																				>
																					<div className="font-medium text-gray-900">
																						{cls.name}
																					</div>
																					<div className="text-xs text-gray-500">
																						ID: {cls.id}
																					</div>
																				</button>
																			))
																		) : (
																			<div className="px-4 py-3 text-center text-gray-500">
																				No classes found
																			</div>
																		)}
																	</div>
																)}
															</div>
														</div>
													</div>
												</div>
											))}

											{/* Add Line Button */}
											<div className="flex gap-3">
												<button
													type="button"
													onClick={addExpenseLine}
													disabled={
														submitting ||
														expenseList.length >= MAX_EXPENSE_LINES
													}
													className="mt-4 px-4 py-2 bg-blue-50 text-blue-600 border border-blue-300 rounded-lg font-medium hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
												>
													+ Add Empty Expense Line
												</button>
												<button
													type="button"
													onClick={copyExpenseLine}
													disabled={
														submitting ||
														expenseList.length === 0 ||
														expenseList.length >= MAX_EXPENSE_LINES ||
														!expenseList[expenseList.length - 1]
															.selectedAccount ||
														!expenseList[expenseList.length - 1].amount ||
														!expenseList[expenseList.length - 1]
															.selectedDepartment
													}
													className="mt-4 px-4 py-2 bg-green-50 text-green-600 border border-green-300 rounded-lg font-medium hover:bg-green-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
												>
													📋 Copy this Expense Line
												</button>
											</div>
										</div>

										{/* Submit Button */}
										<div className="flex gap-4">
											<button
												type="submit"
												disabled={
													submitting ||
													loading ||
													!selectedVendor ||
													!receiveByDate ||
													!selectedLocation ||
													!selectedDepartment ||
													(expenseList.some((line: ExpenseLine) =>
														accountsRequiringProductionLine.some((accountNum) =>
															line.accountSearchText.startsWith(accountNum),
														),
													) &&
														!selectedProductionLine) ||
													expenseList.some(
														(line: ExpenseLine) =>
															!line.selectedAccount ||
															!line.amount ||
															!line.memo,
													)
												}
												className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center"
											>
												{submitting ? (
													<>
														<span className="inline-block animate-spin mr-2">
															⏳
														</span>
														{editMode ? "Updating PO..." : "Creating PO..."}
													</>
												) : editMode ? (
													poApprovalStatus === "Rejected" ? (
														"Update Purchase Order and re-submit"
													) : (
														"Update Purchase Order"
													)
												) : (
													"Create Purchase Order"
												)}
											</button>
											<a
												href="/"
												className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
											>
												Cancel
											</a>
										</div>
									</form>
								</>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	);
};
